import React from "react";
import { ShieldAlert, Clock, CalendarClock, Filter, ChevronRight } from "lucide-react";

const META = {
  blocked:    { color: "#ef4444", label: "Blocked",  Icon: ShieldAlert },
  overdue:    { color: "#ea580c", label: "Overdue",  Icon: Clock },
  "due-soon": { color: "#f59e0b", label: "Due Soon", Icon: CalendarClock },
};
const SIZES = {
  sm: { badge: 28, icon: 15, count: "text-[17px]", pad: "py-1.5 pl-1.5 pr-3", gap: "gap-2.5", radius: "rounded-[10px]" },
  md: { badge: 34, icon: 18, count: "text-[22px]", pad: "py-1.5 pl-1.5 pr-4", gap: "gap-2.5", radius: "rounded-[11px]" },
};

export default function AttentionChip({ kind, label, count, color, icon: IconProp, iconNode, active = false, severe = false, gradient = false, trailing = "filter", size = "sm", onClick, testId }) {
  const m = META[kind];
  const c = color || (m && m.color) || "#ef4444";
  const Icon = IconProp || (m && m.Icon) || ShieldAlert;
  const lbl = label != null ? label : (m ? m.label : "");
  const s = SIZES[size] || SIZES.sm;
  const strong = active || severe;

  // Depth comes from .rl-tile (lifted rest / hover) in App.css; an ACTIVE
  // filter presses IN via .rl-pressed + the colour-tinted inset below.
  let background, borderColor, boxShadow, badgeBg;
  if (gradient) {
    background = `radial-gradient(120% 140% at 0% 50%, ${c}${active ? "33" : "29"}, transparent 58%), hsl(var(--card))`;
    borderColor = active ? `${c}66` : `${c}2e`;
    boxShadow = active ? `inset 0 2px 5px ${c}30, 0 0 0 1px ${c}33` : undefined;
    badgeBg = `${c}29`;
  } else {
    background = strong ? `${c}12` : "hsl(var(--card))";
    borderColor = strong ? `${c}59` : (iconNode ? `${c}40` : "hsl(var(--border))");
    boxShadow = active ? `inset 0 2px 5px ${c}30` : undefined;
    badgeBg = iconNode
      ? (strong ? "hsl(var(--muted))" : "hsl(var(--muted) / 0.7)")
      : (strong ? `${c}29` : `${c}1f`);
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId || `attn-${kind}`}
      title={active && typeof lbl === "string" ? `Clear ${lbl} filter` : undefined}
      className={`group inline-flex items-center ${s.gap} ${s.pad} ${s.radius} border shrink-0 rl-tile ${active ? "rl-pressed" : ""} ${severe ? "animate-pulse-subtle" : ""}`}
      style={{ background, borderColor, boxShadow }}
    >
      <span className="flex items-center justify-center shrink-0 rounded-full rl-ico-well"
        style={{ width: s.badge, height: s.badge, background: badgeBg }}>
        {iconNode ? iconNode : <Icon style={{ color: c, width: s.icon, height: s.icon }} aria-hidden="true" />}
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] whitespace-nowrap"
          style={{ color: active ? c : "hsl(var(--muted-foreground))" }}>{lbl}</span>
        <span className={`font-mono-data font-extrabold tabular-nums ${s.count}`}
          style={{ color: c, letterSpacing: "-0.02em" }}>{count}</span>
      </span>
      {trailing === "filter" ? (
        <Filter
          className={`w-3 h-3 shrink-0 ml-0.5 transition-opacity duration-150 ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          style={{ color: c, fill: active ? c : "transparent" }}
          aria-hidden="true"
        />
      ) : trailing === "chevron" ? (
        <ChevronRight className="w-3.5 h-3.5 shrink-0 ml-0.5 text-[hsl(var(--muted-foreground))]/50" aria-hidden="true" />
      ) : null}
    </button>
  );
}
